import queryString from "query-string";

const { parse, stringify } = queryString;

// CREW-1024 — the vessels filter lives in the URL, the contract /vacancies
// (CREW-1169) and /task-manager (CREW-1040) already follow:
//   URL --getFilterByLocationSearch--> filter  (on LOCATION_CHANGE)
//   filter --getQueryByFilter--> URL           (on APPLY_FILTER)
//
// Keys are grouped by shape, because the shape decides how a value is spelled
// in the address bar. The lists start small on purpose: each filter ticket of
// the epic (CREW-1028 / 1029 / 1030) appends its keys here and nowhere else —
// the chips, the API mapping and the drawer all read these arrays.
//
//   MULTI  — react-select option lists; the URL carries ids only, so a shared
//            link stays short and free of label text (labels come back through
//            withLabelsFrom or the option lists).
//   RANGE  — {from, to}; written as two keys, `dwt_from` / `dwt_to`, so the
//            URL reads like the API call it turns into.
//   TEXT   — a free string, trimmed.
//   FLAG   — on or absent, never "off" (there is no negative form to query).
//   ENUM   — one value out of a fixed list; anything else is junk from a
//            hand-edited URL and is dropped rather than sent to the backend.
export const MULTI_KEYS = ["fleet_type", "type", "owner"];
export const RANGE_KEYS = ["dwt", "age"];
// A name typed or picked from the suggestions; the backend matches it with a
// LIKE (CREW-1027), so a partial name is a valid value.
export const TEXT_KEYS = ["engine", "ism", "commercial", "reg_owner"];
// "Including history" for a name key — read together with it, meaningless alone.
export const FLAG_KEYS = ["ism_history", "commercial_history", "reg_owner_history"];
export const ENUM_KEYS = {
  // "In management": the drawer offers Yes / No; the backend's `is_managed`
  // filter has a third value, `all`, which is what an absent key means.
  managed: ["true", "false"],
  // CREW-1243 — the `_AD_…` service vessels are marked, not hidden, so the list
  // needs a way to ask for just them (checking the four are set up right) and a
  // way to ask for the real fleet only (reading a fleet list without them).
  // Absent means both, like `managed`.
  advertising: ["true", "false"],
};
// The free-text search box. It is a filter like any other and belongs in the
// address bar with them: what the list shows must be what the URL says, or a
// shared link means something different for every person who opens it — it was
// seeded from localStorage before, so a colleague's link silently re-applied
// whatever you last typed. Kept out of the chips row, because the search box
// itself already shows its value (CREW-1243 follow-up).
export const SEARCH_KEY = "search";

export const FILTER_KEYS = [
  SEARCH_KEY,
  ...MULTI_KEYS,
  ...RANGE_KEYS,
  ...TEXT_KEYS,
  ...FLAG_KEYS,
  ...Object.keys(ENUM_KEYS),
];

const isBlank = (value) =>
  value === undefined || value === null || `${value}`.trim().length === 0;

const optionValue = (option) =>
  option && typeof option === "object" ? option.value : option;

/**
 * getFilterByLocationSearch
 * Parse a location.search string into the vessels filter object. Unknown keys
 * and malformed values are ignored, so the result is always safe to send.
 * @param {String} search location.search ("?a=b" or "a=b")
 * @returns {Object} filter
 */
export const getFilterByLocationSearch = (search) => {
  const params = parse(search || "", { arrayFormat: "comma" });
  const filter = {};

  MULTI_KEYS.forEach((key) => {
    const raw = params[key];
    if (isBlank(raw)) return;
    const values = Array.isArray(raw) ? raw : [raw];
    const options = values.filter((v) => !isBlank(v)).map((v) => ({ value: `${v}` }));
    if (options.length) filter[key] = options;
  });

  RANGE_KEYS.forEach((key) => {
    const from = params[`${key}_from`];
    const to = params[`${key}_to`];
    const range = {};
    if (!isBlank(from) && /^\d+$/.test(`${from}`)) range.from = `${from}`;
    if (!isBlank(to) && /^\d+$/.test(`${to}`)) range.to = `${to}`;
    if (Object.keys(range).length) filter[key] = { from: "", to: "", ...range };
  });

  TEXT_KEYS.forEach((key) => {
    if (!isBlank(params[key])) filter[key] = `${params[key]}`.trim();
  });

  FLAG_KEYS.forEach((key) => {
    if (`${params[key]}` === "true") filter[key] = true;
  });

  Object.entries(ENUM_KEYS).forEach(([key, allowed]) => {
    if (allowed.includes(`${params[key]}`)) filter[key] = `${params[key]}`;
  });

  if (!isBlank(params[SEARCH_KEY])) filter[SEARCH_KEY] = `${params[SEARCH_KEY]}`.trim();

  return filter;
};

/**
 * withLabelsFrom
 * Carry `label` across from a filter we already hold onto one just parsed out
 * of the URL, matching on `value` — the URL carries ids only, and without this
 * our own Apply would blank the chips (CREW-1208 on /vacancies).
 * @param {Object} filter - freshly parsed from the URL
 * @param {Object} previous - the filter already in the store
 * @returns {Object}
 */
export const withLabelsFrom = (filter = {}, previous = {}) =>
  Object.entries(filter).reduce((acc, [key, value]) => {
    if (!MULTI_KEYS.includes(key) || !Array.isArray(value)) {
      acc[key] = value;
      return acc;
    }

    const labels = new Map(
      ((previous || {})[key] || []).map((option) => [`${option.value}`, option.label]),
    );

    acc[key] = value.map((option) => {
      const label = labels.get(`${option.value}`);
      return label === undefined ? option : { ...option, label };
    });

    return acc;
  }, {});

/**
 * getQueryByFilter
 * Serialise the vessels filter into a stable (alphabetically sorted) query
 * string. Empty values are omitted, so "no filters" is a clean /vessels URL.
 * @param {Object} filter
 * @returns {String} query string without the leading "?"
 */
export const getQueryByFilter = (filter = {}) => {
  const query = {};

  MULTI_KEYS.forEach((key) => {
    const value = filter[key];
    if (value === undefined || value === null) return;
    const options = Array.isArray(value) ? value : [value];
    // Sorted, so the same selection always spells the same URL whatever order
    // the user ticked the boxes in.
    const ids = options
      .map(optionValue)
      .filter((id) => !isBlank(id))
      .map((id) => `${id}`)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    if (ids.length) query[key] = ids;
  });

  RANGE_KEYS.forEach((key) => {
    const range = filter[key] || {};
    if (!isBlank(range.from)) query[`${key}_from`] = `${range.from}`;
    if (!isBlank(range.to)) query[`${key}_to`] = `${range.to}`;
  });

  TEXT_KEYS.forEach((key) => {
    if (!isBlank(filter[key])) query[key] = `${filter[key]}`.trim();
  });

  FLAG_KEYS.forEach((key) => {
    const on = filter[key];
    if (on === true || `${on}` === "true") query[key] = "true";
  });

  Object.entries(ENUM_KEYS).forEach(([key, allowed]) => {
    const value = optionValue(filter[key]);
    if (allowed.includes(`${value}`)) query[key] = `${value}`;
  });

  if (!isBlank(filter[SEARCH_KEY])) query[SEARCH_KEY] = `${filter[SEARCH_KEY]}`.trim();

  return stringify(query, {
    arrayFormat: "comma",
    sort: (a, b) => a.localeCompare(b),
  });
};
